import {
  createContext,
  ReactNode,
  useContext,
  useRef,
  useState,
} from "react";
import { useTheme } from "@mui/material/styles";
import MDSnackbar from "components/MDSnackbar";
import { v4 as uuidv4 } from "uuid";

type NotificationData = {
  icon?: string;
  title?: string;
  message: string;
};
export type { NotificationData };

type NotificationContextType = {
  showNotification: (data: NotificationData, duration?: number) => void;
};

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error("useNotification must be used within NotificationProvider");
  return context;
};

type NotificationWithId = NotificationData & { id: string };

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<NotificationWithId[]>([]);
  const timers = useRef<Record<string, NodeJS.Timeout>>({});
  const theme = useTheme();

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (timers.current[id]) {
      clearTimeout(timers.current[id]);
      delete timers.current[id];
    }
  };

  const showNotification = (data: NotificationData, duration?: number) => {
    const id = uuidv4();
    const newNotification: NotificationWithId = {
      id,
      icon: data.icon || "notifications",
      title: data.title || "お知らせ",
      message: data.message,
    };

    setNotifications((prev) => {
      const updated = [newNotification, ...prev];
      return updated.slice(0, 5); // 最大5件まで
    });
     if (typeof duration === "number") {
      timers.current[id] = setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {theme &&
        [...notifications].reverse().map((notification, index) => {
          const color = notification.icon === "error" ? "error" : "info";

          return (
            <MDSnackbar
              key={notification.id}
              icon={notification.icon}
              title={notification.title}
              content={notification.message}
              color={color}
              dateTime=""
              open={true}
              onClose={() => removeNotification(notification.id)}
              close={() => removeNotification(notification.id)}
              sx={{
                position: "fixed",
                bottom: `${24 + index * 75}px`,
                right: { xs: "50%", sm: "50%", md: "24px" },
                transform: { xs: "translateX(50%)", sm: "translateX(50%)", md: "none" },
                zIndex: 9999,
                width: { xs: "calc(100% - 32px)", sm: "calc(100% - 32px)", md: "auto" },
                maxWidth: 360,
              }}
            />
          );
        })}
    </NotificationContext.Provider>
  );
};
